import { Divider, Stack } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getGitLabRepositoryBranches,
  getProjectGitBranches,
  ProjectRepo,
  qk,
  updateGitRepository,
} from 'api'
import { DialogContent, ToastContent } from 'elements/feedback'
import { Autocomplete, renderTextField, TextField } from 'elements/inputs'
import { getInputError, useValidations } from 'lib/form-utils'
import { useBoolean, useSearch } from 'lib/hooks'
import { useInfinitePaginatedQuery } from 'lib/react-query-utils'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

const formId = 'EDIT_GIT_INTEGRATION_FORM'

type FormValues = {
  baseBranch: string
  pathTemplate: string
}

type Props = {
  type: 'GITHUB' | 'GITLAB'
  projectId: string
  projectRepo: ProjectRepo
  onClose: () => void
}

export const EditGitIntegration = ({
  type,
  projectId,
  projectRepo,
  onClose,
}: Props) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const rules = useValidations()

  const isBranchFirstSelect = useBoolean(true)

  const { repoId, repo, baseBranch, pathTemplate, username } = projectRepo
  const repoName = repo.replace(`${username}/`, '')

  const { search: searchBranches, onChange: onBranchSearch } = useSearch()

  const $updateGitRepository = useMutation(updateGitRepository)

  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      baseBranch,
      pathTemplate,
    },
  })

  const branch = watch('baseBranch')

  const $githubBranches = useInfinitePaginatedQuery(
    qk.projects.branchesList,
    getProjectGitBranches,
    {
      pagination: {
        page: 0,
        pageSize: 50,
      },
      projectId,
      repoId,
      search: searchBranches,
    },
    {
      enabled: repoId !== '' && type === 'GITHUB',
      onSuccess: branches => {
        if (isBranchFirstSelect.isTrue) {
          setValue('baseBranch', branches.pages[0]?.rows[0] ?? '', {
            shouldValidate: false,
          })
        }
      },
    },
  )

  const $gitlabBranches = useInfinitePaginatedQuery(
    qk.gitlab.branches,
    getGitLabRepositoryBranches,
    {
      pagination: {
        page: 0,
        pageSize: 50,
      },
      projectId,
      repoId,
      repoName: repo,
      search: searchBranches,
    },
    {
      enabled: type === 'GITLAB',
      onSuccess: branches => {
        if (isBranchFirstSelect.isTrue) {
          setValue('baseBranch', branches.pages[0]?.rows[0] ?? '', {
            shouldValidate: false,
          })
        }
      },
      onError: error => {
        toast.error(
          <ToastContent title={(error as { message: string }).message} />,
        )
      },
    },
  )

  return (
    <DialogContent
      title={t('projects.settings.integrations.edit_integration')}
      isActionLoading={$updateGitRepository.isLoading}
      onDeny={onClose}
      externalFormId={formId}
      confirmText={t('common.save')}
    >
      <Divider sx={{ my: 2 }} />
      <Stack
        id={formId}
        component="form"
        onSubmit={handleSubmit(values =>
          $updateGitRepository.mutate(
            {
              projectId,
              repoId,
              ...values,
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries(
                  qk.projects.reposList.toKeyWithArgs({ projectId }),
                )
                onClose()
                toast.success(
                  <ToastContent
                    title={t(
                      'projects.settings.integrations.successfully_edit_message',
                    )}
                  />,
                )
              },
            },
          ),
        )}
        spacing={2}
        mt={2}
      >
        {type === 'GITHUB' && (
          <TextField
            value={username}
            disabled
            label={t('projects.settings.integrations.github_username')}
            onChange={() => {}}
          />
        )}

        <TextField
          value={repoName}
          disabled
          label={t(
            `projects.settings.integrations.repository_name.${type.toLowerCase()}`,
          )}
          onChange={() => {}}
        />

        {type === 'GITHUB' ? (
          <Controller
            name="baseBranch"
            control={control}
            rules={rules.required}
            render={({ field, fieldState }) => (
              <Autocomplete
                label={t('projects.settings.integrations.base_branch')}
                disableInternalFilter
                disableAlphabeticalOrder
                options={
                  $githubBranches.data?.pages.flatMap(page =>
                    page.rows.map(branch => ({
                      value: branch,
                      label: branch,
                    })),
                  ) ?? []
                }
                hasMoreOptions={$githubBranches.hasNextPage}
                onLoadMore={$githubBranches.fetchNextPage}
                isLoadingMore={
                  $githubBranches.isFetchingNextPage ||
                  $githubBranches.isLoading
                }
                {...getInputError(fieldState.error)}
                {...field}
                onInputChange={value => {
                  if (isBranchFirstSelect.isTrue) {
                    isBranchFirstSelect.setFalse()
                  } else {
                    onBranchSearch(value)
                  }
                }}
                onChange={event => {
                  field.onChange(event)
                  onBranchSearch('')
                }}
              />
            )}
          />
        ) : (
          <Controller
            name="baseBranch"
            control={control}
            rules={rules.required}
            render={({ field, fieldState }) => (
              <Autocomplete
                label={t('projects.settings.integrations.base_branch')}
                disableInternalFilter
                disableAlphabeticalOrder
                options={
                  $gitlabBranches.data?.pages.flatMap(page =>
                    page.rows.map(branch => ({
                      value: branch,
                      label: branch,
                    })),
                  ) ?? []
                }
                hasMoreOptions={$gitlabBranches.hasNextPage}
                onLoadMore={$gitlabBranches.fetchNextPage}
                isLoadingMore={
                  $gitlabBranches.isFetchingNextPage ||
                  $gitlabBranches.isLoading
                }
                {...getInputError(fieldState.error)}
                {...field}
                onInputChange={value => {
                  if (isBranchFirstSelect.isTrue) {
                    isBranchFirstSelect.setFalse()
                  } else {
                    onBranchSearch(value)
                  }
                }}
                onChange={event => {
                  field.onChange(event)
                  onBranchSearch('')
                }}
              />
            )}
          />
        )}

        <Controller
          name="pathTemplate"
          control={control}
          rules={{ ...rules.pathTemplate, ...rules.required }}
          render={renderTextField({
            disabled: branch === '',
            label: t('projects.settings.integrations.path_template'),
          })}
        />
      </Stack>
    </DialogContent>
  )
}
